import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";


const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = String(Math.floor(i / 2)).padStart(2, "0");
  const minutes = i % 2 === 0 ? "00" : "30";
  return `${hours}:${minutes}`;
});

const HotelAdd_Information_5 = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { hotelData, loading, saveStep, fetchDraft } = useHotel();
  const [formData, setFormData] = useState({
    operationStart: "",
    operationEnd: "",
    checkinTime: "",
    checkoutTime: "",
    openOnHoliday: "",
    holidayCheckinTime: "",
    holidayCheckoutTime: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id && !hotelData.hours) {
      fetchDraft(id);
    }
  }, [id, hotelData.hours, fetchDraft]);

  useEffect(() => {
    if (hotelData.hours) {
      setFormData({
        ...hotelData.hours,
        openOnHoliday: hotelData.hours.openOnHoliday === true ? "yes" : "no",
      });
    }
  }, [hotelData.hours]);

  const TIME_PLACEHOLDERS = {
    operationStart: "10:30",
    operationEnd: "18:30",
    checkinTime: "11:30",
    checkoutTime: "17:30",
    holidayCheckinTime: "11:30",
    holidayCheckoutTime: "17:30",
  };

  const renderTimeValue = (field) => (selected) =>
    selected ? (
      selected
    ) : (
      <Box component="span" sx={{ color: "#9e9e9e" }}>
        {TIME_PLACEHOLDERS[field]}
      </Box>
    );

  const isTimeOrderInvalid = (start, end) => Boolean(start && end && end <= start);

  const operationTimeError = isTimeOrderInvalid(formData.operationStart, formData.operationEnd);
  const checkoutTimeError = isTimeOrderInvalid(formData.checkinTime, formData.checkoutTime);
  const holidayCheckoutTimeError = isTimeOrderInvalid(formData.holidayCheckinTime, formData.holidayCheckoutTime);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);
      const dataToSend = {
        ...formData,
        openOnHoliday: formData.openOnHoliday === "yes",
      };
      await saveStep("hours", dataToSend);
      navigate(`/hotel/add/license-verification/${id}`);
    } catch (err) {
      console.error("Failed to save step:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      const dataToSend = {
        ...formData,
        openOnHoliday: formData.openOnHoliday === "yes",
      };
      await saveStep("hours", dataToSend);
      navigate(`/hotel/onboarding`);
    } catch (err) {
      console.error("Failed to save step:", err);
    }
  };

  const selectSx = {
    bgcolor: "white",
    borderRadius: "10px",
    height: isMobile ? "45px" : "56px",
    fontSize: isMobile ? "14px" : "16px",
  };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index === 0 ? "#31BACD" : "transparent",
                    border: index === 0 ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index === 0 ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === 0 ? "600" : "400",
                    color: index === 0 ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Box sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: isMobile ? "20px" : "24px",
                color: "#000",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {t('HotelInfoStep5Title')}
              <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                {t('MustFill')} <span style={{ color: "red" }}>*</span><span> )</span>
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: isMobile ? "13px" : "14px",
                color: "#666",
              }}
            >
              {t('HotelInfoStep5Desc')}
            </Typography>
          </Box>

          {/* Form fields */}
          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mt: 4, width: "100%" }}>
              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                  {t('OperationHoursLabel')} <span style={{ color: "red" }}>*</span>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", }}>
                  <Box sx={{ display: "flex", flexDirection: "column", width: "48%", gap: 1 }}>
                    <Select
                      displayEmpty
                      value={formData.operationStart}
                      onChange={(e) => handleInputChange("operationStart", e.target.value)}
                      renderValue={renderTimeValue("operationStart")}
                      sx={{ ...selectSx, flex: 1, }}
                    >
                      <MenuItem value="" disabled>{t('PleaseSelect')}</MenuItem>
                      {TIME_OPTIONS.map((time) => (
                        <MenuItem key={time} value={time}>{time}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", width: "48%", gap: 1 }}>
                    <Select
                      displayEmpty
                      value={formData.operationEnd}
                      onChange={(e) => handleInputChange("operationEnd", e.target.value)}
                      renderValue={renderTimeValue("operationEnd")}
                      sx={{ ...selectSx, flex: 1 }}
                    >
                      <MenuItem value="" disabled>{t('PleaseSelect')}</MenuItem>
                      {TIME_OPTIONS.map((time) => (
                        <MenuItem key={time} value={time}>{time}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
                {operationTimeError && (
                  <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#D32F2F", mt: 1 }}>
                    {t('OperationEndTimeError')}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                  {t('CheckinCheckoutLabel')} <span style={{ color: "red" }}> *</span>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", }}>
                  <Box sx={{ display: "flex", flexDirection: "column", width: "48%", gap: 1 }}>
                    <label htmlFor="checkin-time" style={{ fontWeight: "400", fontSize: isMobile ? "14px" : "16px", color: "#7d7d7d", mb: 1 }}>{t('CheckinLabel')}</label>
                    <Select
                      displayEmpty
                      value={formData.checkinTime}
                      onChange={(e) => handleInputChange("checkinTime", e.target.value)}
                      renderValue={renderTimeValue("checkinTime")}
                      sx={{ ...selectSx, flex: 1 }}
                    >
                      <MenuItem value="" disabled>{t('PleaseSelect')}</MenuItem>
                      {TIME_OPTIONS.map((time) => (
                        <MenuItem key={time} value={time}>{time}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", width: "48%", gap: 1 }}>
                    <label htmlFor="checkout-time" style={{ fontWeight: "400", fontSize: isMobile ? "14px" : "16px", color: "#7d7d7d", mb: 1 }}>{t('CheckoutLabel')}</label>
                    <Select
                      displayEmpty
                      value={formData.checkoutTime}
                      onChange={(e) => handleInputChange("checkoutTime", e.target.value)}
                      renderValue={renderTimeValue("checkoutTime")}
                      sx={{ ...selectSx, flex: 1 }}
                    >
                      <MenuItem value="" disabled>{t('PleaseSelect')}</MenuItem>
                      {TIME_OPTIONS.map((time) => (
                        <MenuItem key={time} value={time}>{time}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
                {checkoutTimeError && (
                  <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#D32F2F", mt: 1 }}>
                    {t('CheckoutTimeError')}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                  {t('OpenOnHolidayLabel')} <span style={{ color: "red" }}> *</span>
                </Typography>
                <RadioGroup
                  row
                  value={formData.openOnHoliday}
                  onChange={(e) => handleInputChange("openOnHoliday", e.target.value)}
                >
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio
                        sx={{
                          color: "#D1D1D1",
                          "&.Mui-checked": { color: "#31BACD" },
                        }}
                      />
                    }
                    label={t('Yes')}
                    sx={{ color: "#000", fontSize: isMobile ? "14px" : "16px" }}
                  />
                  <FormControlLabel
                    value="no"
                    control={
                      <Radio
                        sx={{
                          color: "#D1D1D1",
                          "&.Mui-checked": { color: "#31BACD" },
                        }}
                      />
                    }
                    label={t('No')}
                    sx={{ color: "#000", fontSize: isMobile ? "14px" : "16px" }}
                  />
                </RadioGroup>

                {formData.openOnHoliday === "yes" && (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", width: "48%", gap: 1 }}>
                      <label htmlFor="holiday-checkin-time" style={{ fontWeight: "400", fontSize: isMobile ? "14px" : "16px", color: "#7d7d7d", mb: 1 }}>
                        {t('HolidayCheckinLabel')} <span style={{ color: "red" }}> *</span>
                      </label>
                      <Select
                        displayEmpty
                        value={formData.holidayCheckinTime}
                        onChange={(e) => handleInputChange("holidayCheckinTime", e.target.value)}
                        renderValue={renderTimeValue("holidayCheckinTime")}
                        sx={{ ...selectSx, flex: 1 }}
                      >
                        <MenuItem value="" disabled>{t('PleaseSelect')}</MenuItem>
                        {TIME_OPTIONS.map((time) => (
                          <MenuItem key={time} value={time}>{time}</MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", width: "48%", gap: 1 }}>
                      <label htmlFor="holiday-checkout-time" style={{ fontWeight: "400", fontSize: isMobile ? "14px" : "16px", color: "#7d7d7d", mb: 1 }}>
                        {t('HolidayCheckoutLabel')} <span style={{ color: "red" }}> *</span>
                      </label>
                      <Select
                        displayEmpty
                        value={formData.holidayCheckoutTime}
                        onChange={(e) => handleInputChange("holidayCheckoutTime", e.target.value)}
                        renderValue={renderTimeValue("holidayCheckoutTime")}
                        sx={{ ...selectSx, flex: 1 }}
                      >
                        <MenuItem value="" disabled>{t('PleaseSelect')}</MenuItem>
                        {TIME_OPTIONS.map((time) => (
                          <MenuItem key={time} value={time}>{time}</MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Box>
                )}
                {formData.openOnHoliday === "yes" && holidayCheckoutTimeError && (
                  <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#D32F2F", mt: 1 }}>
                    {t('HolidayCheckoutTimeError')}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={
                !formData.operationStart ||
                !formData.operationEnd ||
                !formData.checkinTime ||
                !formData.checkoutTime ||
                !formData.openOnHoliday ||
                operationTimeError ||
                checkoutTimeError ||
                (formData.openOnHoliday === "yes" &&
                  (!formData.holidayCheckinTime || !formData.holidayCheckoutTime || holidayCheckoutTimeError)) ||
                loading
              }
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/hotel/add/info4/" + id)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={
                  !formData.operationStart ||
                  !formData.operationEnd ||
                  !formData.checkinTime ||
                  !formData.checkoutTime ||
                  !formData.openOnHoliday ||
                  operationTimeError ||
                  checkoutTimeError ||
                  (formData.openOnHoliday === "yes" &&
                    (!formData.holidayCheckinTime || !formData.holidayCheckoutTime || holidayCheckoutTimeError)) ||
                  isSaving
                }
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_Information_5;

